import { ApolloError, useQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';

import { STATUS } from '../screens/InterstitialModal/InterstitialModal.graphql';
import {
  InterstitialStatusResponse,
  Status
} from '../screens/InterstitialModal/InterstitialModal.types';
import useHomeMarket from 'hooks/useHomeMarket';
import { usePatient } from 'hooks/usePatient/usePatient';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { filterConsentsWithLinkUrl, getConsentsWithoutLinkUrl } from 'utilities/consent';

export const useInterstitialStatus = (): {
  status: Status | null;
  loading: boolean;
  error?: ApolloError;
} => {
  const { isLoggedIn } = usePatient();
  const { id } = useHomeMarket();
  const { data, loading, error } = useQuery<InterstitialStatusResponse>(STATUS, {
    skip: !isLoggedIn,
    onError: err => {
      BugTracker.notify(err, 'Failed to fetch interstitial status');
    }
  });
  const loggedInvalidConsents = useRef<string>('');
  const requiredConsents = data?.interstitialStatus?.requiredConsents || [];
  const consentsWithoutLinkUrl = getConsentsWithoutLinkUrl(requiredConsents);
  const invalidConsentIds = consentsWithoutLinkUrl.map(consent => consent.id).join(',');

  useEffect(() => {
    if (!invalidConsentIds || loggedInvalidConsents.current === invalidConsentIds) return;

    loggedInvalidConsents.current = invalidConsentIds;
    BugTracker.notify(
      new Error(`Invalid interstitial required consents without linkUrl: ${invalidConsentIds}`),
      'InvalidInterstitialRequiredConsents'
    );
  }, [invalidConsentIds]);

  return {
    status:
      !data || !id
        ? null
        : {
            ...data.interstitialStatus,
            requiredConsents: filterConsentsWithLinkUrl(requiredConsents)
          },
    loading,
    error
  };
};
